import './playerView.css';

import React, { useMemo } from 'react';
import { useEffect, useState } from 'react';
import { useAuthContext } from '../../../common/contexts/useAuthContext';
import { useRecoilState, useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { tenantScopesState } from '../../recoil/atoms/tenantScopesState';
import { tenantSettingsState } from '../../recoil/atoms/tenantSettingsState';
import { selectedPlayerState } from '../../recoil/atoms/selectedPlayerState';
import { teamsState } from '../../recoil/atoms/teamsState';
import { playerOverviewsState } from '../../recoil/atoms/playerOverviewsState';
import { playersState } from '../../recoil/atoms/playersState';

import LockIcon from '@mui/icons-material/Lock';
import BlockIcon from '@mui/icons-material/Block';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import PermScanWifiRoundedIcon from '@mui/icons-material/PermScanWifiRounded';


import { translate } from '../../../common/language/translations';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';
import { getIndexColor } from '../../utils/colorUtils';
import { ProgressCircle } from '../display/visualization/ProgressCircle';
import { PositionFieldPlot } from '../plots/PositionFieldPlot';
import { ClubLogo } from '../display/images/ClubLogo';
import { CountryFlag } from '../display/images/CountryFlag';
import { countryCodeToCountryInfo } from '../../static/countries';
import { IconButton } from '../controls/buttons/IconButton';
import { PlayerImage } from '../display/images/PlayerImage';
import { getTenantColor } from '../../static/tenantConfigs';
import { ClubHistoryEntry } from './PlayerView';
import { getPlayingTimeIcon } from '../../utils/iconUtils';
import { Rating } from '@mui/material';
import { connectPlayerWithoutData, deletePlayerFromPlatform } from '../../services/firestore/players';
import { PlayerFinding } from '../../views/scouting/playerFinding/PlayerFinding';
import { FindPlayerTable } from '../tables/findPlayerTable/FindPlayerTable';
import { TextButton } from '../controls/buttons/TextButton';
import { CompetitionFlag } from '../display/images/CompetitionFlag';
import { playerMetricMap } from '../../static/playerMetrics';
import { getDisplayPositions, getPositionsToCompareAgainst } from '../../utils/positionUtils';
import { IndexBar } from '../display/visualization/IndexBar';
import { getRoundedIndexValue, isLimitedRoleConfig, roleConfigIsBeingComputed } from '../../utils/metricUtils';
import { Club } from '../../types/club';
import { PlayerClubIteration, PlayerEntry, PlayerDocument, PlayerId, PlayerOverview, PositionGroupIndex } from '../../types/player';
import { RoleConfig, RoleConfigMap } from '../../types/tenant';
import { getStrictPlayerId } from '../../utils/playerUtils';
import { getAge } from '../../utils/ageUtils';
import { getDisplayPlayerName } from '../../utils/nameUtils';
import { useSetSelectedPlayer } from '../../recoil/hooks/useSetSelectedPlayer';
import { PlatformPosition } from '../../types/position';
import { getFormationConfig } from '../../utils/formationUtils';


interface MergedClubHistoryEntry {
  club: Club;
  containsCurrentPeriod: boolean;
  periods: ClubHistoryEntry[];
}


interface MergedNationalHistoryEntry {
  countryCode: string;
  appearances?: number;
  ageSpecificAppearances?: number;
}


interface PlayerDataToCompare {
  id: PlayerId;
  name: string;
  positionInOwnTeam?: PlatformPosition;

  event_data_available: boolean;
  event_data_exist: boolean;

  age?: number;
  countryCode?: string;

  imageUrl?: string;
  primary_positions?: string[];

  indexes: { [key: string]: PositionGroupIndex }
}


interface IndexEntry {
  id: string;
  name: string;
  index: number;
  confidence: number;
  possibleConfidence: number;

  description?: string;
  roleConfig?: RoleConfig;
}


interface PlayerViewOverviewProps {
  isExistingPlayerWithoutData: boolean;
  setIsConnectingPlayer: (value: boolean) => void;

  clubHistory: ClubHistoryEntry[] | undefined;
  nationalHistory: ClubHistoryEntry[] | undefined;
  iterationsWithEventData: PlayerClubIteration[] | undefined;

  averageScoutingReportOverallScore: number | undefined; // undefined until report data is loaded
  numberOfScoutingReportsWithOverallScore: number | undefined; // undefined if no scouting reports with overall score exist
}

export const PlayerViewOverview: React.FC<PlayerViewOverviewProps> = ({
  isExistingPlayerWithoutData,
  setIsConnectingPlayer,

  clubHistory,
  nationalHistory,
  iterationsWithEventData,

  averageScoutingReportOverallScore,
  numberOfScoutingReportsWithOverallScore,
}) => {

  const { currentUser } = useAuthContext();
  const { openConfirmModal, openTextModal, openRoleInfoModal } = useOpenGlobalModal();

  const handleOpenInfoModal = (titleKey: string) => {
    openTextModal(
      translate(titleKey, userConfig?.language),
      translate(titleKey + 'Info', userConfig?.language),
      { infoType: 'infoIcon', titleKey }
    );
  };

  const teams = useRecoilValue(teamsState);
  const playerOverviews = useRecoilValue(playerOverviewsState);
  const players = useRecoilValue(playersState);
  const userConfig = useRecoilValue(userConfigState);
  const tenantScopes = useRecoilValue(tenantScopesState);
  const tenantSettings = useRecoilValue(tenantSettingsState);

  const [selectedPlayer, setSelectedPlayer] = useRecoilState(selectedPlayerState);
  const { updateSelectedPlayer } = useSetSelectedPlayer();

  const [selectedPlayerToMap, setSelectedPlayerToMap] = useState<PlayerOverview | undefined>(undefined);
  const [isAnyFilterExpanded, setIsAnyFilterExpanded] = useState(false);

  const [allPlayersToCompareAgainst, setAllPlayersToCompareAgainst] = useState<PlayerDataToCompare[] | undefined>(undefined);
  const [sortedPlayersToCompareAgainst, setSortedPlayersToCompareAgainst] = useState<PlayerDataToCompare[] | undefined>(undefined);
  const [positionsToCompareAgainst, setPositionsToCompareAgainst] = useState<PlatformPosition[] | undefined>(undefined);

  const selectedIndexToCompare = useMemo(() => {
    if (selectedPlayer?.selectedOverallIndex) return selectedPlayer.selectedOverallIndex;
    return {
      id: 'skill_index',
      name: translate('skillIndex', userConfig?.language),
    };
  }, [selectedPlayer?.selectedOverallIndex, userConfig?.language]);


  const [mergedClubHistory, setMergedClubHistory] = useState<MergedClubHistoryEntry[] | undefined>(undefined);
  const [mergedNationalHistory, setMergedNationalHistory] = useState<MergedNationalHistoryEntry[] | undefined>(undefined);

  const [showConfidence, setShowConfidence] = useState(false);


  const confirmDeletion = () => {
    if (!selectedPlayer || !userConfig) return;
    deletePlayerFromPlatform(selectedPlayer.id, userConfig.email, userConfig.club, currentUser);
    setSelectedPlayer(undefined);
  };


  const connectPlayer = async () => {
    if (!selectedPlayer || !userConfig || !selectedPlayerToMap) return;

    setIsConnectingPlayer(true);

    const currentPlayerId = getStrictPlayerId(selectedPlayer.id);

    const newPlayerId = selectedPlayerToMap.id;
    const newPlayerName = selectedPlayerToMap.name;

    await connectPlayerWithoutData(
      currentPlayerId,
      newPlayerId,
      newPlayerName,
      userConfig,
      currentUser,
    );

    setSelectedPlayer({
      id: newPlayerId,
      name: newPlayerName,
      playerOverview: selectedPlayerToMap
    });

    setSelectedPlayerToMap(undefined);

    setIsConnectingPlayer(false);
  };


  const getOverallData = (playerOverview: PlayerOverview | undefined) => {
    if (!playerOverview) return undefined;
    return playerOverview.position_group_stats?.['OVERALL'] ?? playerOverview.position_group_stats?.[playerOverview.position_groups[0]];
  };


  const getRawPlayerDataToCompare = (
    overview: PlayerOverview | undefined,
    document: PlayerDocument | undefined,
    positionInOwnTeam?: PlatformPosition,
    entry?: PlayerEntry,
  ): PlayerDataToCompare | undefined => {

    const id = overview?.id ?? entry?.id;
    const name = overview?.name ?? entry?.name;

    if (!id || !name) return undefined;

    const birth_date = document?.birth_date ?? overview?.birth_date;
    const overallData = getOverallData(overview);

    const eventDataAvailable = overview?.event_data_available ?? false;

    const indexes: { [key: string]: PositionGroupIndex } = {};

    // overall indexes
    if (overallData?.skill_index) indexes.skill_index = overallData.skill_index;
    if (overallData?.physical_index) indexes.physical_index = overallData.physical_index;

    // position group indexes
    const positionGroups = overview?.position_groups ?? [];
    for (const positionGroup of positionGroups) {
      const positionGroupData = overview?.position_group_stats?.[positionGroup];
      if (positionGroupData?.custom_indexes) {
        Object.entries(positionGroupData.custom_indexes).forEach(([key, value]) => {
          indexes[key] = value;
        });
      }
    }

    return {
      id,
      name,
      positionInOwnTeam,

      event_data_available: eventDataAvailable,
      event_data_exist: overview?.event_data_exist ?? false,

      primary_positions: overview?.position_info.primary_positions,
      age: birth_date ? getAge(birth_date) : undefined,
      countryCode: document?.country_code ?? overview?.country_code,
      imageUrl: overview?.image_url,

      indexes: indexes,
    };
  };


  useEffect(() => {
    if (selectedPlayer?.playerOverview && playerOverviews && players && teams?.ownTeam) {
      const playersToCompare: PlayerDataToCompare[] = [];

      const allPositionsToCompareAgainst = new Set<PlatformPosition>();
      const positionGroupsPlayed = selectedPlayer.playerOverview.position_groups;
      const primaryPosition = selectedPlayer.playerOverview.position_info.primary_positions[0];

      positionGroupsPlayed.forEach(positionGroup => {
        const positionsToCompareAgainst = getPositionsToCompareAgainst(
          positionGroup,
          getFormationConfig(teams.ownTeam.formation, tenantSettings?.formationConfigs),
          primaryPosition,
        );
        positionsToCompareAgainst.forEach(position => allPositionsToCompareAgainst.add(position));
      });

      allPositionsToCompareAgainst.forEach(position => {
        const playerList = teams.ownTeam.players?.[position];
        playerList?.forEach(playerEntry => {
          const overview = !isNaN(Number(playerEntry.id)) ? playerOverviews[Number(playerEntry.id)] : undefined;
          const playerDataToCompare = getRawPlayerDataToCompare(overview, players[playerEntry.id], position, playerEntry);
          if (playerDataToCompare) {
            playersToCompare.push(playerDataToCompare);
          }
        });
      });

      // insert selected player into the sorted list, unless it is already in the list
      if (playersToCompare.findIndex(player => player.id === selectedPlayer.id) === -1) {
        const playerDataToCompare = getRawPlayerDataToCompare(selectedPlayer.playerOverview, players[selectedPlayer.id]);
        if (playerDataToCompare) {
          playersToCompare.push(playerDataToCompare);
        }
      }

      setAllPlayersToCompareAgainst(playersToCompare);
    }
  }, [selectedPlayer?.id, selectedPlayer?.playerOverview, playerOverviews, players, teams?.ownTeam]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (allPlayersToCompareAgainst && teams?.ownTeam?.formation) {

      const positionGroupOfSelectedIndex = selectedIndexToCompare.roleConfig
        ? selectedIndexToCompare.roleConfig.positionGroup
        : selectedPlayer?.playerOverview?.position_groups[0];

      const positionsToCompareAgainst = positionGroupOfSelectedIndex
        ? getPositionsToCompareAgainst(
          positionGroupOfSelectedIndex,
          getFormationConfig(teams.ownTeam.formation, tenantSettings?.formationConfigs),
          selectedPlayer?.playerOverview?.position_info.primary_positions[0],
        )
        : [];

      const playersToCompare = allPlayersToCompareAgainst.filter(player => {
        return player.id === selectedPlayer?.id || (player.positionInOwnTeam && positionsToCompareAgainst.includes(player.positionInOwnTeam));
      });

      const sortedPlayers = [...playersToCompare].sort((a, b) => {
        const aIndex = a.indexes[selectedIndexToCompare.id]?.index;
        const bIndex = b.indexes[selectedIndexToCompare.id]?.index;

        if (aIndex === undefined && bIndex === undefined) return 0;
        if (aIndex === undefined) return 1;
        if (bIndex === undefined) return -1;
        return bIndex - aIndex;
      });

      setSortedPlayersToCompareAgainst(sortedPlayers);
      setPositionsToCompareAgainst(positionsToCompareAgainst);
    }
  },
  [
    allPlayersToCompareAgainst,
    selectedIndexToCompare,
    tenantSettings?.formationConfigs,
    selectedPlayer?.playerOverview?.position_groups,
    selectedPlayer?.playerOverview?.position_info.primary_positions,
    selectedPlayer?.id,
    teams?.ownTeam?.formation,
  ]);


  useEffect(() => {
    if (clubHistory && nationalHistory) {

      const newMergedClubHistory: MergedClubHistoryEntry[] = [];
      const newMergedNationalHistory: MergedNationalHistoryEntry[] = [];

      clubHistory.forEach(entry => {
        const existingEntry = newMergedClubHistory.find(item => item.club.id === entry.club.id);
        if (existingEntry) {
          existingEntry.periods.push(entry);
          if (entry.isCurrentPeriod) {
            existingEntry.containsCurrentPeriod = true;
          }
        }
        else {
          newMergedClubHistory.push({
            club: entry.club,
            periods: [entry],
            containsCurrentPeriod: entry.isCurrentPeriod ?? false,
          });
        }
      });

      nationalHistory.forEach(entry => {
        const existingEntry = newMergedNationalHistory.find(item => item.countryCode === entry.club.country_code);

        const clubName = entry.club.name; // will only exist for age-specific teams

        if (existingEntry) {
          if (clubName) {
            existingEntry.ageSpecificAppearances = (existingEntry.ageSpecificAppearances ?? 0) + entry.basic_stats.appearances;
          }
          else {
            existingEntry.appearances = (existingEntry.appearances ?? 0) + entry.basic_stats.appearances;
          }
        }
        else {
          if (clubName) {
            newMergedNationalHistory.push({
              countryCode: entry.club.country_code,
              ageSpecificAppearances: entry.basic_stats.appearances,
            });
          }
          else {
            newMergedNationalHistory.push({
              countryCode: entry.club.country_code,
              appearances: entry.basic_stats.appearances,
            });
          }
        }
      });

      setMergedClubHistory(newMergedClubHistory);
      setMergedNationalHistory(newMergedNationalHistory);
    }
  }, [clubHistory, nationalHistory]);


  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfidence(true);
    }, 200);

    return () => clearTimeout(timer);
  }, [selectedPlayer?.playerOverview]);


  const maxNumberOfIndexEntries = 4;
  const indexEntries: IndexEntry[] = useMemo(() => {

    const overallData = getOverallData(selectedPlayer?.playerOverview);

    const indexEntries: IndexEntry[] = [{
      id: 'skill_index',
      name: playerMetricMap['skill_index'].name[userConfig?.language ?? 'en'],
      description: playerMetricMap['skill_index'].description[userConfig?.language ?? 'en'],

      index: getRoundedIndexValue(overallData?.skill_index?.index),
      confidence:overallData?.skill_index?.confidence ?? 0,
      possibleConfidence: overallData?.skill_index?.possible_confidence ?? 0,
    }];

    if (tenantScopes?.hasSkillcorner) {
      indexEntries.push({
        id: 'physical_index',
        name: playerMetricMap['physical_index'].name[userConfig?.language ?? 'en'],
        description: playerMetricMap['physical_index'].description[userConfig?.language ?? 'en'],

        index: getRoundedIndexValue(overallData?.physical_index?.index),
        confidence: overallData?.physical_index?.confidence ?? 0,
        possibleConfidence: overallData?.physical_index?.possible_confidence ?? 0,
      });
    }

    // we allow a total of 4 index entries, and we want to fill the list with role indexes, ordered by confidence first, then by index
    const allRoleIndexEntries: IndexEntry[] = [];

    const roleConfigs: RoleConfigMap = tenantSettings?.roleConfigs ?? {};
    const positionGroupToRoleIds: Record<string, string[]> = tenantSettings?.positionGroupToRoleIds ?? {};

    const positionGroups = selectedPlayer?.playerOverview?.position_groups ?? [];
    for (const positionGroup of positionGroups) {

      const customIndexesForPositionGroup = selectedPlayer?.playerOverview?.position_group_stats?.[positionGroup]?.custom_indexes;
      const roleIdsForPositionGroup = positionGroupToRoleIds?.[positionGroup];

      if (customIndexesForPositionGroup && roleIdsForPositionGroup) {

        roleIdsForPositionGroup.forEach(roleId => {
          const roleConfig = roleConfigs[roleId];
          const positionGroupIndex = customIndexesForPositionGroup[roleId];
          if (roleConfig && positionGroupIndex) {

            const isBeingComputed = roleConfigIsBeingComputed(roleConfig);

            if (!isBeingComputed) {
              allRoleIndexEntries.push({
                id: roleId,
                name: roleConfig.name,
                roleConfig: roleConfig,

                index: getRoundedIndexValue(positionGroupIndex.index),
                confidence: positionGroupIndex.confidence ?? 0,
                possibleConfidence: positionGroupIndex.possible_confidence ?? 0,
              });
            }
          }
        });
      }
    }

    allRoleIndexEntries.sort((a, b) => {
      if (a.confidence !== b.confidence) {
        return b.confidence - a.confidence;
      }
      else {
        return (b.index ?? 0) - (a.index ?? 0);
      }
    });

    allRoleIndexEntries.forEach(entry => {
      if (entry.confidence > 0 && indexEntries.length < maxNumberOfIndexEntries) {
        indexEntries.push(entry);
      }
    });

    return indexEntries;
  }, [selectedPlayer?.playerOverview, userConfig?.language, tenantScopes?.hasSkillcorner, tenantSettings?.roleConfigs, tenantSettings?.positionGroupToRoleIds]);


  const overallPossibleSkillIndexConfidence = useMemo(() => {
    const overallData = getOverallData(selectedPlayer?.playerOverview);
    return overallData?.skill_index?.possible_confidence ?? 0;
  }, [selectedPlayer?.playerOverview]);


  const isGoalkeeper = selectedPlayer?.playerOverview?.position_info.primary_positions.includes('GK')
    || selectedPlayer?.playerOverview?.position_info.primary_positions.includes('G');


  const tenantColor = getTenantColor(userConfig?.club ?? '');


  return (
    <div className='full-size-container'>

      {isAnyFilterExpanded && <div className='filter-section-empty-background' onClick={() => setIsAnyFilterExpanded(false)} />}

      <div className={'player-view-overview-top-row' + (isExistingPlayerWithoutData ? ' player-view-overview-top-row-player-without-data' : '')}>

        {selectedPlayer?.playerOverview?.event_data_available && (
          <div
            className='player-view-widget-container player-view-widget-container-absolute player-view-overview-column player-view-overview-left-and-middle-column'
          >
            <div className='player-view-overview-widget-title'>
              {translate('overallIndexes', userConfig?.language)}

              <IconButton
                icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
                onClick={() => handleOpenInfoModal('overallIndexes')}
                size={18}
                style={{ marginLeft: 6 }}
              />
            </div>

            <div className='player-view-overview-index-row'>

              {indexEntries.map((entry, index) => {

                if (isGoalkeeper && entry.id === 'physical_index') {
                  return null;
                }

                return (
                  <React.Fragment key={index}>

                    {index !== 0 && (
                      <div className='player-view-overview-index-divider' />
                    )}

                    <div className='player-view-overview-index-entry'>
                      <div
                        className='player-view-overview-index-entry-name flex-row align-center'
                        onClick={() => entry.roleConfig ? openRoleInfoModal(entry.roleConfig) : openTextModal(entry.name, entry.description ?? '')}
                        title={translate('showDescription', userConfig?.language)}
                      >
                        {entry.roleConfig && isLimitedRoleConfig(entry.roleConfig) && (
                          <PermScanWifiRoundedIcon style={{ fontSize: 11, marginRight: 2, color: '#ffffff99' }} />
                        )}
                        {entry.name}
                      </div>
                      <div
                        className='player-view-overview-index-entry-value-row'
                        style={{
                          borderColor: selectedIndexToCompare.id === entry.id ? tenantColor : 'transparent',
                          borderRadius: selectedIndexToCompare.id === entry.id ? '4px 4px 0px 0px' : 4,
                        }}
                        onClick={() => updateSelectedPlayer(
                          selectedPlayer,
                          { selectedOverallIndex: { id: entry.id, name: entry.name, roleConfig: entry.roleConfig } }
                        )}
                        title={translate('useAsComparisonBasis', userConfig?.language)}
                      >
                        {entry.confidence > 0 && (
                          <div
                            className='flex-column align-center justify-center'
                            style={{ color: getIndexColor(entry.index, 60), fontSize: 19 }}
                          >
                            {entry.index}

                            <div className='flex-row align-center justify-center' style={{ height: 14, marginTop: 4 }}>
                              <IndexBar
                                index={showConfidence ? entry.index : 0}
                                confidence={entry.confidence}
                                color={50}
                                secondDimension={'height'}
                                trailColor='#50566b'
                                border='0.5px solid #50566b'
                                duration={0.4}
                              />
                            </div>
                          </div>
                        )}

                        {entry.confidence > 0 && (
                          <div
                            className='flex-row align-center justify-center'
                            style={{ marginLeft: 16 }}
                          >
                            <ProgressCircle
                              size={23}
                              progress={showConfidence ? entry.confidence * 100 : 0}
                              color={getIndexColor(entry.confidence * 10, 60)}
                              duration={0.6}
                              value={Math.round(entry.confidence * 100)}
                              fontSize={11}
                              trailColor='#50566b'
                            />
                          </div>
                        )}

                        {entry.confidence === 0 && (
                          <div className='flex-row align-center justify-center' >
                            {entry.possibleConfidence > 0 && (
                              <LockIcon style={{ fontSize: 20, color: '#ffffff66' }} />
                            )}

                            {entry.possibleConfidence === 0 && (
                              <BlockIcon style={{ fontSize: 20, color: '#ffffff66' }} />
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {!selectedPlayer?.playerOverview?.event_data_available && (
          <div className={
            'player-view-widget-container player-view-widget-container-absolute'
            + (isExistingPlayerWithoutData
              ? ' player-view-overview-left-and-middle-column'
              : ' player-view-overview-left-half-column')
          }>

            {isExistingPlayerWithoutData && (
              <div className='full-size flex-row align-center'>
                <div className='player-view-overview-without-data-description'>
                  <div style={{ fontSize: 15, color: '#ffffffee' }}>
                    {translate('playerWasAddedWithoutData', userConfig?.language)}
                  </div>
                  <div style={{ fontSize: 13, color: '#ffffffbb' }}>
                    {translate('playerWasAddedWithoutDataDescription', userConfig?.language)}
                  </div>
                </div>

                <TextButton
                  onClick={() => openConfirmModal(confirmDeletion, 'deletePlayer', 'deletePlayerInfo')}
                  text={translate('deletePlayer', userConfig?.language)}
                  icon={<DeleteForeverIcon style={{ fontSize: 20 }} />}
                  buttonType={'dark'}
                  style={{ height: 32, borderRadius: 4, margin: '0 4%' }}
                />
              </div>
            )}

            {!isExistingPlayerWithoutData && !selectedPlayer?.playerOverview?.event_data_exist && (
              <div className='player-view-overview-without-event-data-description'>
                <BlockIcon style={{ fontSize: 24, color: '#ffffff99' }} />
                {translate('playerHasNoEventData', userConfig?.language)}
              </div>
            )}

            {!isExistingPlayerWithoutData && selectedPlayer?.playerOverview?.event_data_exist && (
              <div className='player-view-overview-without-event-data-description'>
                <LockIcon style={{ fontSize: 24, color: '#ffffff99', marginTop: -3 }} />
                {translate('youDoNotHaveEventDataForThisPlayer', userConfig?.language)}
              </div>
            )}
          </div>
        )}

        <div className={
          'player-view-widget-container player-view-widget-container-absolute player-view-overview-column'
          + (isExistingPlayerWithoutData || selectedPlayer?.playerOverview?.event_data_available
            ? ' player-view-overview-right-column'
            : ' player-view-overview-right-half-column')
        }>
          <div className='player-view-overview-widget-title'>
            {translate('averageReportScore', userConfig?.language)}

            <IconButton
              icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
              onClick={() => handleOpenInfoModal('averageReportScore')}
              size={18}
              style={{ marginLeft: 6 }}
            />
          </div>

          {userConfig?.accessConfig.reportAccess !== 'none' && numberOfScoutingReportsWithOverallScore !== undefined && (
            <div
              className='player-view-overview-column-content fade-in'
              style={{ flexDirection: 'column', justifyContent: 'center', gap: isExistingPlayerWithoutData ? 9 : undefined }}
            >
              <Rating
                name='overallScore'
                value={averageScoutingReportOverallScore}
                max={5}
                precision={0.5}
                sx={{
                  fontSize: 28,
                  color: tenantColor,
                  '& .MuiRating-iconEmpty': {
                    color: '#9d9fa3'
                  },
                }}
                readOnly={true}
              />

              {numberOfScoutingReportsWithOverallScore > 0 && (
                <div style={{ fontSize: 12, color: '#ffffffcc' }}>
                  {translate('basedOn<positions>', userConfig?.language)}
                  {' ' + numberOfScoutingReportsWithOverallScore + ' '}
                  {translate(numberOfScoutingReportsWithOverallScore > 1 ? 'scoutingReports' : 'scoutingReport', userConfig?.language, true)}
                </div>
              )}

              {numberOfScoutingReportsWithOverallScore === 0 && (
                <div style={{ fontSize: 12, color: '#ffffffcc' }}>
                  {translate('thePlayerHasNoScoutingReports', userConfig?.language)}
                </div>
              )}

            </div>
          )}

          {userConfig?.accessConfig.reportAccess === 'none' && (
            <div className='player-view-overview-column-content' style={{ justifyContent: 'center' }}>
              <LockIcon style={{ fontSize: 24, color: '#ffffff99' }} />
            </div>
          )}
        </div>
      </div>

      {!isExistingPlayerWithoutData && (
        <div className='player-view-overview-bottom-row'>

          {selectedPlayer?.playerOverview?.event_data_exist && (
            <div className='player-view-widget-container player-view-widget-container-absolute player-view-overview-column player-view-overview-left-column'>

              {selectedPlayer?.playerOverview?.event_data_available && (
                <div className='player-view-overview-widget-title'>
                  {translate('comparison', userConfig?.language) + ': ' + selectedIndexToCompare.name}
                  <div style={{ fontSize: 10, color: '#ffffffaa', marginBottom: -1, marginLeft: 4 }}>
                    {'(' + getDisplayPositions(positionsToCompareAgainst, userConfig?.language) + ')'}
                  </div>

                  <IconButton
                    icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
                    onClick={() => handleOpenInfoModal('comparison')}
                    size={18}
                    style={{ marginLeft: 6 }}
                  />
                </div>
              )}

              {selectedPlayer?.playerOverview?.event_data_available && sortedPlayersToCompareAgainst && playerOverviews && (
                <div className='player-view-overview-column-content player-view-overview-column-scrollable' style={{ gap: 0, paddingTop: 0, paddingBottom: 10 }}>
                  {sortedPlayersToCompareAgainst.map(player => {

                    const positionGroupIndex = player.indexes[selectedIndexToCompare.id];

                    const index = getRoundedIndexValue(positionGroupIndex?.index);
                    const confidence = positionGroupIndex?.confidence ?? 0;
                    const possibleConfidence = positionGroupIndex?.possible_confidence ?? 0;

                    return (
                      <div
                        key={player.id + '-' + selectedIndexToCompare.id}
                        className='player-view-overview-comparison-entry-container'
                      >
                        <div
                          className='player-view-overview-comparison-entry'
                          style={{
                            border: player.id === selectedPlayer?.id ? 'solid 0.5px ' + tenantColor : undefined,
                            boxShadow: player.id === selectedPlayer?.id ? '0px 0px 0px 0.5px ' + tenantColor : undefined,
                          }}
                        >
                          <PlayerImage
                            src={player.imageUrl}
                            size={34}
                            containerStyle={{ marginRight: 9 }}
                            isSingleImage={true}
                          />

                          <div className='player-view-overview-comparison-entry-info-column'>
                            <div className='flex-row align-center' style={{ fontSize: 14, color: '#ffffffee' }}>
                              {getDisplayPlayerName(player.name, 16)}

                              <CountryFlag
                                countryCode={player.countryCode}
                                width={14}
                                containerStyle={{ marginLeft: 8 }}
                              />
                            </div>

                            <div className='flex-row align-center' style={{ fontSize: 12, color: '#ffffffcc', marginRight: 12, gap: 10 }}>
                              {getDisplayPositions(player.primary_positions, userConfig?.language)}

                              <div>
                                {player.age}
                                {player.age ? (' ' + translate('years', userConfig?.language, true)) : '-'}
                              </div>
                            </div>
                          </div>


                          {confidence > 0 && (
                            <div className='flex-row align-center'>
                              <div
                                className='flex-column align-center justify-center'
                                style={{
                                  minWidth: 60,
                                  color: getIndexColor(index, 60),
                                  fontSize: 15
                                }}
                              >
                                {index}

                                <div className='flex-row align-center justify-center' style={{ marginTop: 5 }}>
                                  <IndexBar
                                    index={showConfidence ? index: 0}
                                    confidence={confidence}
                                    color={'#c2d3e6'}
                                    maxWidth={50}
                                    height={2}
                                    secondDimension={'opacity'}
                                    trailColor={'#50566b'}
                                    duration={0.4}
                                  />
                                </div>
                              </div>

                              <div
                                className='flex-row align-center justify-center'
                                style={{ minWidth: 32 }}
                              >
                                <ProgressCircle
                                  size={21}
                                  progress={showConfidence ? confidence * 100 : 0}
                                  color={getIndexColor(confidence * 10, 60)}
                                  duration={0.6}
                                  value={Math.round(confidence * 100)}
                                  fontSize={10}
                                  trailColor='#50566b'
                                />
                              </div>
                            </div>
                          )}

                          {possibleConfidence > 0 && confidence === 0 && (
                            <div className='flex-row align-center justify-center' style={{ minWidth: 32 }}>
                              <LockIcon style={{ fontSize: 20, color: '#ffffff77', marginTop: -1 }} />
                            </div>
                          )}

                          {possibleConfidence === 0 && (
                            <div className='flex-row align-center justify-center' style={{ minWidth: 32 }}>
                              <BlockIcon style={{ fontSize: 20, color: '#ffffff77' }} />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}


              {!selectedPlayer?.playerOverview?.event_data_available && (
                <div className='player-view-overview-widget-title'>
                  {translate('seasonsWithEventData', userConfig?.language)}

                  <IconButton
                    icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
                    onClick={() => handleOpenInfoModal('seasonsWithEventData')}
                    size={18}
                    style={{ marginLeft: 6 }}
                  />
                </div>
              )}

              {!selectedPlayer?.playerOverview?.event_data_available && iterationsWithEventData !== undefined && (
                <div
                  className='player-view-overview-column-content fade-in'
                  style={{ paddingLeft: 0, paddingRight: 0 }}
                >
                  <div
                    className='player-view-overview-seasons-with-event-data-container player-view-overview-column-scrollable'
                    style={{ paddingLeft: 8, paddingRight: 10 }}
                  >
                    {iterationsWithEventData?.map((iteration, index) => {

                      const minutesPlayed = iteration.basic_stats.minutes_played;
                      const minutesColorIndex = Math.min(minutesPlayed / 150, 10);
                      const minutesColor = minutesColorIndex ? getIndexColor(minutesColorIndex, 60) : 'transparent';

                      return (
                        <div
                          key={index}
                          className='flex-row align-center'
                          style={{
                            width: '100%',
                            justifyContent: 'space-between',
                            gap: 6,
                            height: 50,
                          }}
                        >
                          <div
                            className='flex-column'
                            style={{
                              alignItems: 'flex-start',
                              gap: 4,
                              fontSize: 12,
                              color: '#ffffffdd',
                            }}
                          >
                            <CompetitionFlag
                              competitionId={iteration.competition_id}
                              width={14}
                              seasonToShow={iteration.season}
                              containerStyle={{ gap: 4, marginLeft: 1 }}
                              imageStyle={{ marginRight: 3 }}
                              smallFontSize={[11]}
                              smallFontSizeThreshold={[20]}
                            />

                            <ClubLogo
                              club={iteration.club}
                              size={14}
                              showClubName={true}
                              containerStyle={{ gap: 7, marginLeft: 1 }}
                              smallFontSize={11}
                              smallFontSizeThreshold={20}
                            />
                          </div>

                          <div className='flex-row align-center' style={{ gap: 10 }}>
                            <div style={{ fontSize: 11, color: '#ffffffcc' }}>
                              {minutesPlayed} min
                            </div>

                            <div style={{ paddingBottom: 8 }}>
                              <ProgressCircle
                                size={14}
                                progress={minutesColorIndex * 10}
                                color={minutesColor}
                                strokeWidth={9}
                                trailColor='#ffffff44'
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className='player-view-overview-career-entry-divider' />

                  <div className='flex-row align-center' style={{ minHeight: 30, gap: 10, paddingTop: 5 }}>
                    <div style={{ fontSize: 13, color: '#ffffffdd' }}>
                      {translate('possibleConfidence', userConfig?.language)}
                    </div>

                    <ProgressCircle
                      size={26}
                      progress={showConfidence ?overallPossibleSkillIndexConfidence * 100 : 0}
                      color={getIndexColor(overallPossibleSkillIndexConfidence * 10, 60)}
                      duration={0.6}
                      value={Math.round(overallPossibleSkillIndexConfidence * 100)}
                      fontSize={11}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className={
            'player-view-widget-container player-view-widget-container-absolute player-view-overview-column'
            + (selectedPlayer?.playerOverview?.event_data_exist ? ' player-view-overview-middle-column' : ' player-view-overview-left-half-column')
          }>
            <div className='player-view-overview-widget-title'>
              {translate('positionsPlayed', userConfig?.language)}

              <IconButton
                icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
                onClick={() => handleOpenInfoModal('positionsPlayed')}
                size={18}
                style={{ marginLeft: 6 }}
              />
            </div>

            <div className='player-view-overview-column-content' style={{ padding: 5 }}>
              {selectedPlayer?.playerOverview && (
                <div className='full-size' style={{ marginTop: -5 }}>
                  <PositionFieldPlot
                    positions={selectedPlayer?.playerOverview.position_info.positions}
                    language={userConfig?.language ?? 'en'}
                    showConfidence={showConfidence}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={
            'player-view-widget-container player-view-widget-container-absolute player-view-overview-column'
            + (selectedPlayer?.playerOverview?.event_data_exist ? ' player-view-overview-right-column' : ' player-view-overview-right-half-column')
          }>

            <div className='player-view-overview-widget-title'>
              {translate('career', userConfig?.language)}

              <IconButton
                icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
                onClick={() => handleOpenInfoModal('career')}
                size={18}
                style={{ marginLeft: 6 }}
              />
            </div>

            {mergedNationalHistory && mergedClubHistory && (
              <div className='player-view-overview-column-content player-view-overview-column-scrollable'>

                {mergedClubHistory.map((entry, index) => (
                  <div
                    key={index}
                    className='player-view-overview-career-entry'
                    style={{
                      height: 20 + entry.periods.length * 25,
                      padding: !selectedPlayer?.playerOverview?.event_data_exist ? '0px 16px 0px 12px' : '0px 6px 0px 2px',
                    }}
                  >
                    <div className='flex-row align-center full-size' >
                      <ClubLogo
                        clubLogoUrl={entry.club.logo_url}
                        size={26}
                        containerStyle={{ marginRight: 9 }}
                      />

                      <div className='flex-column full-size' style={{ justifyContent: 'space-evenly' }}>
                        <div className='flex-row align-items'>
                          {entry.club.name}
                        </div>

                        {entry.periods.map((period, periodIndex) => {
                          return (
                            <div
                              key={periodIndex}
                              className='player-view-overview-career-entry-period'
                            >
                              <div className='flex-row align-center' style={{ gap: 4 }}>
                                <div>
                                  {period.firstSeason}
                                </div>

                                {(period.isCurrentPeriod || period.lastSeason) && '-'}

                                {period.lastSeason && !period.isCurrentPeriod && (
                                  <div>
                                    {period.lastSeason}
                                  </div>
                                )}

                                {period.isCurrentPeriod
                                && selectedPlayer?.playerOverview?.availability_status
                                && selectedPlayer?.playerOverview.availability_status !== 'UNKNOWN'
                                && getPlayingTimeIcon(selectedPlayer?.playerOverview.availability_status, 15, '#ffffffcc')}
                              </div>

                              <div>
                                {period.basic_stats.appearances}
                                {' ' + translate(period.basic_stats.appearances === 1 ? 'appearance' : 'appearances', userConfig?.language, true)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}

                {mergedClubHistory.length === 0 && (
                  <div style={{ fontSize: 12, color: '#ffffffcc' }}>
                    {translate('noClubCareer', userConfig?.language)}
                  </div>
                )}

                <div className='player-view-overview-career-entry-divider-container'>
                  <div className='player-view-overview-career-entry-divider' />
                </div>

                {mergedNationalHistory.map((entry, index) => (
                  <div
                    key={index}
                    className='player-view-overview-career-entry'
                    style={{
                      height: 20 + (entry.appearances !== undefined && entry.ageSpecificAppearances !== undefined ? 20 : 0),
                      padding: !selectedPlayer?.playerOverview?.event_data_exist ? '0px 16px 0px 12px' : '0px 6px 0px 2px',
                    }}
                  >
                    <CountryFlag
                      countryCode={entry.countryCode}
                      width={22}
                      containerStyle={{ marginLeft: 2, marginRight: 11 }}
                    />

                    <div className='flex-column full-size' style={{ justifyContent: 'space-evenly' }}>
                      {entry.appearances !== undefined && (
                        <div className='flex-row align-center' style={{ justifyContent: 'space-between' }}>
                          <div>
                            {countryCodeToCountryInfo[entry.countryCode]?.name[userConfig?.language ?? 'en']}
                          </div>

                          <div style={{ fontSize: 12, color: '#ffffffcc' }}>
                            {entry.appearances}
                            {' ' + translate(entry.appearances === 1 ? 'appearance' : 'appearances', userConfig?.language, true)}
                          </div>
                        </div>
                      )}

                      {entry.ageSpecificAppearances !== undefined && (
                        <div
                          className='flex-row align-center'
                          style={{ justifyContent: 'space-between', marginTop: entry.appearances !== undefined ? 8 : undefined }}
                        >
                          <div style={{ fontSize: 12, color: '#ffffffcc' }}>
                            {translate('ageSpecificTeams', userConfig?.language)}
                          </div>

                          <div style={{ fontSize: 12, color: '#ffffffcc' }}>
                            {entry.ageSpecificAppearances}
                            {' ' + translate(entry.ageSpecificAppearances === 1 ? 'appearance' : 'appearances', userConfig?.language, true)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {mergedNationalHistory.length === 0 && (
                  <div style={{ fontSize: 12, color: '#ffffffcc' }}>
                    {translate('noNationalCareer', userConfig?.language)}
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      )}

      {isExistingPlayerWithoutData && !selectedPlayerToMap && (
        <div className='player-view-overview-bottom-row player-view-overview-bottom-row-player-without-data player-view-widget-container'>
          <PlayerFinding
            isAnyFilterExpanded={isAnyFilterExpanded}
            setIsAnyFilterExpanded={setIsAnyFilterExpanded}
            handlePlayerClick={setSelectedPlayerToMap}
          />
        </div>
      )}

      {isExistingPlayerWithoutData && selectedPlayerToMap && (
        <div
          className='player-view-overview-bottom-row player-view-overview-bottom-row-player-without-data player-view-widget-container'
          style={{ flexDirection: 'column' }}
        >
          <div className='player-view-overview-table-container'>
            <FindPlayerTable
              data={[selectedPlayerToMap]}
            />
          </div>

          <TextButton
            onClick={() => openConfirmModal(connectPlayer, 'connectPlayer', 'noRegretInfo')}
            text={translate('connectPlayer', userConfig?.language)}
            icon={<CallMergeIcon style={{ fontSize: 24 }} />}
            style={{ margin: 'auto', marginTop: '30vh', height: 34, borderRadius: 4 }}
          />

          <div className='player-view-overview-connect-player-link' onClick={() => setSelectedPlayerToMap(undefined)}>
            {translate('searchAgain?', userConfig?.language)}
          </div>
        </div>
      )}
    </div>
  );
};
